'use client';

/**
 * Enquiries Hook
 * 
 * Provides enquiry management functionality for customers.
 * Uses the CustomerAuthProvider for API access.
 */

import { useState, useCallback, useEffect } from 'react';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import type { Enquiry, CreateEnquiryRequest } from '@/types/customer';

// ============================================================================
// Types
// ============================================================================

export interface UseEnquiriesReturn {
  /** All enquiries for the current customer */
  enquiries: Enquiry[];
  /** Whether enquiries are loading */
  isLoading: boolean;
  /** Error if any */
  error: string | null;
  /** Refresh enquiries */
  refresh: () => Promise<void>;
  /** Get a specific enquiry with messages */
  getEnquiry: (enquiryId: string) => Promise<Enquiry | null>;
  /** Create a new enquiry */
  createEnquiry: (data: CreateEnquiryRequest) => Promise<Enquiry | null>;
  /** Send a message on an enquiry */
  sendMessage: (enquiryId: string, content: string) => Promise<Enquiry | null>;
}

// ============================================================================
// Hook
// ============================================================================

export function useEnquiries(): UseEnquiriesReturn {
  const { getCustomerClient, isAuthenticated } = useCustomerAuth();
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch enquiries
  const refresh = useCallback(async () => {
    const client = getCustomerClient();
    if (!client) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await client.getEnquiries();
      setEnquiries(response?.items || []);
    } catch (err) {
      // Log the error but handle gracefully for API issues
      const errorMessage = err instanceof Error ? err.message : 'Failed to load enquiries';
      console.error('[useEnquiries] Failed to fetch enquiries:', err);
      
      // Don't show error to user for API availability issues
      if (errorMessage.includes('API server unavailable') || 
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('Network')) {
        setEnquiries([]);
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [getCustomerClient]);

  // Load enquiries when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      setEnquiries([]);
    }
  }, [isAuthenticated, refresh]);

  // Get a specific enquiry with messages
  const getEnquiry = useCallback(async (enquiryId: string): Promise<Enquiry | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      return await client.getEnquiry(enquiryId);
    } catch (err) {
      console.error('[useEnquiries] Failed to get enquiry:', err);
      return null;
    }
  }, [getCustomerClient]);

  // Create a new enquiry
  const createEnquiry = useCallback(async (data: CreateEnquiryRequest): Promise<Enquiry | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      const enquiry = await client.createEnquiry(data);
      setEnquiries((prev) => [enquiry, ...prev]);
      return enquiry;
    } catch (err) {
      console.error('[useEnquiries] Failed to create enquiry:', err);
      setError(err instanceof Error ? err.message : 'Failed to create enquiry');
      return null;
    }
  }, [getCustomerClient]);

  // Send a message on an enquiry
  const sendMessage = useCallback(async (
    enquiryId: string,
    content: string
  ): Promise<Enquiry | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      const enquiry = await client.addEnquiryMessage(enquiryId, content);
      setEnquiries((prev) => prev.map((e) => (e.id === enquiryId ? enquiry : e)));
      return enquiry;
    } catch (err) {
      console.error('[useEnquiries] Failed to send message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
      return null;
    }
  }, [getCustomerClient]);

  return {
    enquiries,
    isLoading,
    error,
    refresh,
    getEnquiry,
    createEnquiry,
    sendMessage,
  };
}

export default useEnquiries;
